import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EventCard } from '@/features/events/components/EventCard';
import { toEventCardProps } from '@/features/events/event.mappers';
import { FROM_DASHBOARD } from '@/features/events/event-detail-back';
import { useRegisteredEvents, useUpcomingEvents } from '@/hooks/useEvents';
import { ROUTES } from '@/lib/constants/routes';
import { DASHBOARD_PREVIEW, DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';

const S = DASHBOARD_STRINGS;

/**
 * Live upcoming events preview on the member dashboard (UX sheet,
 * StartupAdminVF r17-r19), from the same endpoint as the events page so the
 * count and rows match it. "View All" lives here only, not on Featured.
 *
 * Uses the events page's `EventCard` (full-width cover photo, title, date,
 * View Details) rather than the old compact row, whose 64px thumbnail and
 * squeezed text the client flagged (UX sheet, StartupAdminVF r19). It also
 * replaces that row's Register/Unregister buttons, which did nothing:
 * registration happens on the event's detail page.
 */
export function DashboardUpcomingEvents() {
  const { data, isLoading, isError, refetch } = useUpcomingEvents(1);
  const { data: registered } = useRegisteredEvents();
  const registeredIds = new Set((registered ?? []).map((event) => event.id));
  const events = (data?.data ?? []).slice(0, DASHBOARD_PREVIEW.UPCOMING_EVENTS);

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full">
      <section aria-labelledby="dashboard-upcoming-heading" className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <h2 id="dashboard-upcoming-heading" className="text-h3 text-teal">
              {S.UPCOMING_TITLE}
            </h2>
            {data && data.total > 0 && (
              <Badge
                variant="green-solid"
                size="count"
                className="rounded-full px-2.5 py-0.5 text-sm font-bold"
              >
                {data.total}
              </Badge>
            )}
          </div>
          <Button asChild variant="link" className="text-green hover:text-dkgreen text-[1.5rem]">
            <Link to={ROUTES.EVENTS}>{S.VIEW_ALL}</Link>
          </Button>
        </div>
        {isLoading ? (
          <Skeleton rows={2} className="h-24 w-full" />
        ) : isError ? (
          <ErrorState description={S.UPCOMING_LOAD_FAILED} onRetry={() => void refetch()} />
        ) : events.length === 0 ? (
          <p className="text-body text-slate">{S.UPCOMING_EMPTY}</p>
        ) : (
          // Columns follow this card's own width, not the viewport's: the
          // dashboard's side column comes and goes, so the same viewport can
          // leave very different room here. One row of three when there is
          // room (wide enough that the date and time stay readable), stacked
          // otherwise, and the fluid cover keeps every photo at the same
          // proportion throughout.
          <div className="@container">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-3">
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={toEventCardProps(event)}
                  isRegistered={registeredIds.has(event.id)}
                  fluidCover
                  showAccessTag={false}
                  linkState={FROM_DASHBOARD}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </Card>
  );
}
